import Auth, { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { Link, navigate } from 'gatsby'
import { TFunction } from 'i18next'
import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import {
  AWS_AUTH_RETRY_CONFIG,
  establishSignInAuth,
  resolveSignInSession,
} from '../../../utils/completeSignUpAuth'
import {
  signInValidation,
  validateEmailDomain,
} from '../../../utils/validation'
import { useAppState } from '../../appState'
import appleIcon from '../../assets/images/apple_logo.svg'
import emailIcon from '../../assets/images/email.svg'
import googleIcon from '../../assets/images/google_logo.svg'
// import facebookIcon from '../../assets/images/facebook_logo.svg'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  sendExtensionMessage,
  sendToExtension,
} from '../../../utils/extensionHelper'
import { TrackingCase } from '../../../utils/trackingCases'
import { buildFinancialData } from '../../../utils/financialData'
import axios from '../../axios'
import { ENDPOINTS, EXTENSION_ID, ROUTES } from '../../constants'
import useReCaptcha from '../../hooks/useReCaptcha'
import { styles } from '../../pagesStyles/joinsStyles'
import { tracker } from '../../systemLogs'
import CHRButton from '../button'
import CHRInput from '../input'
import CHRText from '../typography'
import { checkIfSubmittable } from './../../../utils/validation'
declare const window: any

const eye = <FontAwesomeIcon icon={faEye} />
const eyeSlash = <FontAwesomeIcon icon={faEyeSlash} />

interface SignInProps {
  t: TFunction
  fromGatePage?: boolean
  allowRedirect?: boolean
}

const SignIn = ({ t, fromGatePage, allowRedirect = true }: SignInProps) => {
  const classes = styles()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formErrors, setFormErrors] = useState({
    emailMessage: '',
    passwordMessage: '',
  })
  const [requestError, setRequestError] = useState('')
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const [passwordShown, setPasswordShown] = useState(false)
  const [, dispatch] = useAppState()

  const {
    captchaRef,
    isVerifiedFromClient,
    isClientVerificationInProgress,
    isVerifiedFromServer,
    isServerVerificationInProgress,
    onExpiry,
    onVerify,
  } = useReCaptcha({ customErrorSetter: setRequestError })

  const togglePasswordVisiblity = () => {
    setPasswordShown(!passwordShown)
  }

  const handleSignIn = async (username: string, password: string) => {
    setIsFormSubmitting(true)
    setFormErrors({ emailMessage: '', passwordMessage: '' })

    try {
      // Check for restricted domains
      const domainValidation = await validateEmailDomain(email, 'signin')
      if (!domainValidation.isValid) {
        setFormErrors({
          emailMessage: domainValidation.errorMessage || 'Invalid email domain',
          passwordMessage: '',
        })
        setIsFormSubmitting(false)
        return
      }

      const isCaptchaVerified = isVerifiedFromClient && isVerifiedFromServer

      if (!isCaptchaVerified) {
        setRequestError(t('messages.signInError'))
        return
      }

      await signInValidation({ email, password })

      const normalizedEmail = username.trim().toLowerCase()
      const { data: signInResponse } = await axios.post(ENDPOINTS.signIn, {
        email: normalizedEmail,
        password,
      })

      const { user, authPayload } = await establishSignInAuth(
        signInResponse?.data,
        normalizedEmail,
        password,
        AWS_AUTH_RETRY_CONFIG
      )
      const { accessToken, session, userId } = await resolveSignInSession(
        user,
        authPayload,
        AWS_AUTH_RETRY_CONFIG
      )

      if (session || accessToken) {
        const {
          data: { data },
        } = await axios.get(ENDPOINTS.user.replace(':id', userId), {
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        const currentUserInfo = await Auth.currentCredentials()
        const userProfile = {
          email: data.email,
          userName: data.username,
          fullName: data.name,
          imageKey: data.image,
          imageUrl: '' as Object | string,
          isPro: data.isPro,
          isCreator: data.isCreator,
          isReferralBonus: data.isReferralBonus,
          userDoublePayment: data.userDoublePayment,
          financialData: buildFinancialData(data.financialData),
          loginProvider: data.loginProvider,
          isShortLinkReferralAllowed: data.isShortLinkReferralAllowed,
          shareLinkSegment: data.shareLinkSegment,
          shareLinkDeclaredAt: data.shareLinkDeclaredAt,
          shareLinkReviewStatus: data.shareLinkReviewStatus,
          canCreateForwardableBoards: data.canCreateForwardableBoards || false,
          canCreateEditPage: data.canCreateEditPage || false,
          defaultShoppingBoardView: data.defaultShoppingBoardView,
        }

        if (data.image && currentUserInfo?.identityId) {
          const url = `https://${process.env.GATSBY_S3_BUCKET_NAME}.s3.us-east-2.amazonaws.com/public/${data.image}`
          userProfile.imageUrl = url
        }
        sendExtensionMessage({ session: session || undefined })
        sendToExtension('CHR_AUTH_SESSION', { auth: session || undefined })
        if (window.chrome?.runtime?.sendMessage) {
          window.chrome.runtime.sendMessage(
            EXTENSION_ID,
            { session: session || undefined },
            _response => {}
          )
        }
        dispatch({ type: 'SIGN_IN', auth: user, userProfile })

        // No redirect requested
        if (allowRedirect === false) {
          navigate(0)
          return
        }

        const hashFragment = window.location.href.split('#')[1]?.split('?')[1]
        const hashParams = new URLSearchParams(hashFragment)
        const redirectParam = hashParams.get('redirect')
        // Redirect if rquested using `redirect` param
        if (redirectParam) {
          navigate(redirectParam)
          return
        }

        const urlParams = new URLSearchParams(window.location.search)
        const signInFromExtension = urlParams.get('signInFromExtension')
        // Redirect to home with ext params if login initated from extension
        if (signInFromExtension) {
          navigate(
            `${ROUTES.member}${
              signInFromExtension
                ? '?signInFromExtension=signInFromExtension'
                : ''
            }`
          )
          return
        }

        // Refresh the page after login
        navigate(0)
      } else {
        setRequestError(t('messages.notAuthorized'))
        Auth.signOut()
        sendExtensionMessage({ action: 'signOut' })
        sendToExtension('CHR_SIGN_OUT')
        if (window.chrome?.runtime?.sendMessage) {
          window.chrome.runtime.sendMessage(
            EXTENSION_ID,
            { action: 'signOut' },
            (_response: any) => {}
          )
        }
      }
    } catch (error) {
      if (error.errors) {
        if (error.inner[0]?.params.path === 'email') {
          setFormErrors({
            emailMessage: error.inner[0]?.message,
            passwordMessage: error.inner[1]?.message,
          })
        } else {
          setFormErrors({ passwordMessage: error.inner[0]?.message })
        }
        return
      }
      setRequestError(t('messages.signInError'))
      tracker.track(TrackingCase.UserTracking, `login failed`, {
        errorMessage: error.message,
        email,
        username,
      })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const isSubmitting = isFormSubmitting

  const isLoginDisabled =
    isSubmitting ||
    isClientVerificationInProgress ||
    isServerVerificationInProgress ||
    !isVerifiedFromClient ||
    !isVerifiedFromServer ||
    !email ||
    !password

  return (
    <div className={classes.container}>
      <CHRText align="left">
        {!fromGatePage && (
          <span className={classes.signInText}>
            {t('login.signInToChirpyest')}
          </span>
        )}
      </CHRText>
      <div className={classes.signInWithEmail}>
        {formErrors.emailMessage && (
          <p className={classes.errorMessage}>{formErrors.emailMessage}</p>
        )}
        <CHRInput
          value={email}
          onChange={e => {
            setEmail(e.target.value)
          }}
          placeholder={t('shared.emailAddress')}
          type="email"
          error={formErrors.emailMessage}
          onKeyPress={event =>
            !isSubmitting &&
            checkIfSubmittable(event, () => handleSignIn(email, password))
          }
        />
      </div>
      <div className={classes.signInWithEmail}>
        {formErrors.passwordMessage && (
          <p className={classes.errorMessage}>{formErrors.passwordMessage}</p>
        )}
        <div className={classes.passwordField}>
          <CHRInput
            value={password}
            onChange={e => {
              setPassword(e.target.value)
            }}
            placeholder={t('shared.password')}
            type={passwordShown ? 'text' : 'password'}
            error={formErrors.passwordMessage}
            onKeyPress={event =>
              !isSubmitting &&
              checkIfSubmittable(event, () => handleSignIn(email, password))
            }
          />
          <i
            className={classes.passVisibility}
            onClick={togglePasswordVisiblity}
          >
            {passwordShown ? eye : eyeSlash}
          </i>
        </div>
      </div>

      <div style={{ margin: 'auto', paddingTop: '15px' }}>
        <ReCAPTCHA
          sitekey={String(process.env.GATSBY_RECAPTCHA_SECRET_KEY)}
          onChange={onVerify}
          onExpired={onExpiry}
          ref={captchaRef}
        />
      </div>

      <div className={classes.signInWithEmail}>
        {requestError && <p className={classes.errorMessage}>{requestError}</p>}
        <CHRButton
          onClick={() => handleSignIn(email, password)}
          label={
            isClientVerificationInProgress
              ? 'verifying captcha...'
              : fromGatePage
              ? t('gate.charterSignIn')
              : t('login.signInWithEmail')
          }
          icon={emailIcon}
          isSubmitting={isSubmitting}
          disabled={isLoginDisabled}
        />
        <CHRText size="small">or</CHRText>
        <CHRButton
          onClick={() =>
            Auth.federatedSignIn({
              provider: CognitoHostedUIIdentityProvider.Google,
            })
              .then(cred => {
                console.log(cred)
                window.Sentry.captureMessage('Google Login Success')
                window.Sentry.captureException(cred)
              })
              .catch(e => {
                console.log(e)
                window.Sentry.captureMessage('Google Login Error')
                window.Sentry.captureException(e)
              })
          }
          label={t('login.signInWithGoogle')}
          icon={googleIcon}
          isSubmitting={isSubmitting}
          disabled={isSubmitting}
        />
        {/* <CHRButton
          onClick={() =>
            Auth.federatedSignIn({
              provider: CognitoHostedUIIdentityProvider.Facebook,
            })
          }
          label={t('login.signInWithFacebook')}
          icon={facebookIcon}
          isSubmitting={isSubmitting}
          disabled={isSubmitting}
        /> */}
        <CHRButton
          onClick={() =>
            Auth.federatedSignIn({
              provider: CognitoHostedUIIdentityProvider.Apple,
            })
          }
          label={t('login.signInWithApple')}
          icon={appleIcon}
          isSubmitting={isSubmitting}
          disabled={isSubmitting}
        />
        <div className={classes.bottomStrip}>
          <div>
            no account?{' '}
            <Link
              rel="preconnect"
              to={ROUTES.join}
              className={classes.forgotPassword}
              state={{ joinSourceUrl: location.pathname }}
            >
              {t('login.joinHere')}
            </Link>
          </div>
          <div>
            <Link
              rel="preconnect"
              to={ROUTES.forgotPassword}
              className={classes.forgotPassword}
            >
              {t('login.forgotPassword')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default withTranslation()(SignIn)
